import { useEffect, useRef, useState } from "react";

export default function OtpModal({
  open,
  title = "Verify OTP",
  description = "Enter the 6-digit code sent to your contact detail.",
  contactLabel = "",
  loading = false,
  onClose,
  onVerify,
}) {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [error, setError] = useState("");
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!open) {
      setOtp(new Array(6).fill(""));
      setError("");
    }
  }, [open]);

  const focusNext = (index) => {
    const next = inputsRef.current[index + 1];
    if (next) next.focus();
  };

  const focusPrev = (index) => {
    const prev = inputsRef.current[index - 1];
    if (prev) prev.focus();
  };

  const handleChange = (value, index) => {
    if (value && !/^[0-9]$/.test(value)) return;

    setError("");
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

    if (value) focusNext(index);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      focusPrev(index);
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();

    const next = new Array(6).fill("");
    pasted.split("").forEach((char, index) => {
      next[index] = char;
    });
    setOtp(next);
    const focusIndex = Math.min(pasted.length, 5);
    setTimeout(() => inputsRef.current[focusIndex]?.focus(), 0);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const otpValue = otp.join("");

    if (otpValue.length !== 6 || otp.some((digit) => !digit)) {
      setError("Please enter the complete code");
      return;
    }

    if (typeof onVerify === "function") {
      await onVerify(otpValue);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C4DFF]">OTP Verification</p>
            <h3 className="mt-2 text-2xl font-bold text-gray-900">{title}</h3>
            <p className="mt-2 text-sm text-gray-600">{description}</p>
            {contactLabel && <p className="mt-2 text-sm font-medium text-gray-800">{contactLabel}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close OTP modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 8.586l5.95-5.95a1 1 0 111.414 1.414L11.414 10l5.95 5.95a1 1 0 01-1.414 1.414L10 11.414l-5.95 5.95A1 1 0 012.636 15.95L8.586 10 2.636 4.05A1 1 0 014.05 2.636L10 8.586z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Enter verification code</label>
            <div className="mt-3 flex justify-between gap-2" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(ref) => (inputsRef.current[index] = ref)}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={digit}
                  onChange={(event) => handleChange(event.target.value.replace(/\D/g, ""), index)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  className="h-12 w-12 rounded-xl border border-[#E6E2FF] text-center text-lg font-semibold text-gray-900 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15"
                />
              ))}
            </div>
            {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-2xl border border-gray-200 px-4 py-3 font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-2xl bg-gradient-to-r from-[#7C4DFF] to-[#5D3BFF] px-4 py-3 font-semibold text-white shadow-lg shadow-[#7C4DFF]/20 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
